import { storeToRefs } from 'pinia'
import { computed, ref, type ComputedRef, type Ref } from 'vue'

import { chatModel } from '@/entities/messages'
import { useStoreRooms } from '@/entities/room'


import { useCurrentUser } from '@/shared/composables/current-user'
import { useLocalization } from '@/shared/composables/localization'
import type { RoomListItem } from '@/shared/types/app'

export enum RoomItemFilter {
  ALL = 'ALL',
  UNREAD = 'UNREAD',
  GROUPS = 'GROUPS',
  PERSONAL = 'PERSONAL',
  WHERE_I_AM_MODERATOR = 'WHERE_I_AM_MODERATOR',
  WHERE_I_AM_OWNER = 'WHERE_I_AM_OWNER',
}

export type RoomFilterMenuItem = {
  label: string
  value: RoomItemFilter
  dividerBefore?: boolean
  hidden?: boolean
}

type UseRoomFiltersOptions = {
  rooms?: ComputedRef<RoomListItem[]> | Ref<RoomListItem[]>
}

export const useRoomFilters = (options?: UseRoomFiltersOptions) => {

  const { getUnreadCount } = chatModel.useChatMessageStore()
  const { roomsList } = storeToRefs(useStoreRooms())
  const sourceRooms = computed(() => options?.rooms?.value ?? roomsList.value)
  const { currentUser } = useCurrentUser()
  const { t } = useLocalization()

  const filterItems = ref<RoomFilterMenuItem[]>([
    { label: t('common.roomItemFilterAll'), value: RoomItemFilter.ALL },
    { label: t('common.roomItemFilterUnread'), value: RoomItemFilter.UNREAD, dividerBefore: true },
    { label: t('common.roomItemFilterGroups'), value: RoomItemFilter.GROUPS },
    { label: t('common.roomItemFilterPersonal'), value: RoomItemFilter.PERSONAL },
    { label: t('common.whereIAmModerator'), value: RoomItemFilter.WHERE_I_AM_MODERATOR, dividerBefore: true },
    { label: t('common.whereIAmOwner'), value: RoomItemFilter.WHERE_I_AM_OWNER, hidden: true },
  ])

  const filters = computed(() => filterItems.value.filter((filter) => !filter.hidden))

  const selectedFilter = ref(RoomItemFilter.ALL)

  const isFilterActive = computed(() => selectedFilter.value !== RoomItemFilter.ALL)

  const selectedFilterLabel = computed(() => (
    filterItems.value.find((filter) => filter.value === selectedFilter.value)?.label ?? ''
  ))

  const hasContactRole = (room: RoomListItem, role: 'Moderator' | 'Owner') => (
    !!room.contacts?.find((contact) => (
      contact.role === role && contact.id === currentUser.value?.imLogin
    ))
  )

  const filtered = computed(() => sourceRooms.value.filter((el) => {
    if (selectedFilter.value === RoomItemFilter.UNREAD) {
      return getUnreadCount(el.pServedUser) > 0
    }
    if (selectedFilter.value === RoomItemFilter.GROUPS) {
      return !!el.isGroup
    }
    if (selectedFilter.value === RoomItemFilter.PERSONAL) {
      return !el.isGroup
    }
    if (selectedFilter.value === RoomItemFilter.WHERE_I_AM_MODERATOR) {
      return hasContactRole(el, 'Moderator')
    }
    if (selectedFilter.value === RoomItemFilter.WHERE_I_AM_OWNER) {
      return hasContactRole(el, 'Owner')
    }
    return true
  }))

  const clearFilter = () => {
    selectedFilter.value = RoomItemFilter.ALL
  }

  return {
    filtered,
    filters,
    selectedFilter,
    selectedFilterLabel,
    isFilterActive,
    clearFilter,
  }
}
